import { useMemo } from "react";

import { useEffect, useState, useCallback } from "react";
import { useSearchQuery, useOnQueryChange, useOnQuerySubmit } from "../providers/search_query_provider";

const ALTERNATE_CONTENT_IDX = -1;
const ROTATION_INTERVAL = 2500;

export function useRotatingContent(contentOptions: string[], alternateContent?: string) {
  // Track the displayed content index, reset when the search query changes
  const [contentIdx, setContentIdx] = useState(0);
  const [lastContentIdx, setLastContentIdx] = useState(0);
  const resetContent = useCallback(() => {
    setContentIdx(0);
    setLastContentIdx(0);
  }, []);
  const setSubmittedContent = useCallback(() => {
    if (alternateContent && alternateContent.length) {
      setContentIdx(ALTERNATE_CONTENT_IDX);
    }
  }, [alternateContent]);

  // Use the search query and listen for changes
  const { normalizedQuery } = useSearchQuery();
  useOnQueryChange(resetContent);
  useOnQuerySubmit(setSubmittedContent);

  // Rotate the content at a fixed interval (reset whenever the query changes)
  useEffect(() => {
    // Only start interval if not resetting and not at the last option
    if (contentIdx >= contentOptions.length - 1) {
      return;
    }

    const interval = setInterval(() => {
      const nextIndex = lastContentIdx + 1;
      if (nextIndex >= contentOptions.length) {
        // Stop at the last option instead of looping
        return;
      }

      setContentIdx(nextIndex);
      setLastContentIdx(nextIndex);
    }, ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, [contentOptions.length, contentIdx, normalizedQuery, lastContentIdx]);

  // Extract the content based on the current index
  const content = useMemo(() => {
    if (contentIdx === ALTERNATE_CONTENT_IDX) {
      return alternateContent;
    }
    return contentOptions[contentIdx];
  }, [contentIdx, contentOptions, alternateContent]);

  return content;
}
